/**
 * Layout primitives (spec Amendment A). CardGrid auto-fills from a 300px minimum card above 1440,
 * 3-up at 1024–1439, 2-up at 768–1023, 1-up below. FormColumn caps a form at 720px inside any
 * full-width area; FormColumns puts two side by side when a form is wider. Tabs is the drawer and
 * page tab strip (selection is the rail, never an outline).
 */
import { FeedbackPill } from './feedback';
import * as React from 'react';
import { DisabledWhy } from './Button';
import { disclosed, useDisclosureValue } from './disclosure';

export function CardGrid({ children, className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-kit="cards" className={className ? `k-cards ${className}` : 'k-cards'} {...rest}>
      {children}
    </div>
  );
}

export function Card({ children, className, ...rest }: React.HTMLAttributes<HTMLElement>) {
  return (
    <section data-kit="card" className={className ? `k-card ${className}` : 'k-card'} {...rest}>
      {/* R-15 D2 (T-111): a titled card is a panel — it carries the feedback pill */}
      {rest['aria-labelledby'] ? <FeedbackPill /> : null}
      {children}
    </section>
  );
}

export function FormColumn({ children, className, ...rest }: React.FormHTMLAttributes<HTMLFormElement>) {
  return (
    <form data-kit="form" className={className ? `k-form ${className}` : 'k-form'} {...rest}>
      {children}
    </form>
  );
}

/** A form column that is not a <form> (fields inside a larger form). */
export function FieldColumn({ children }: { children: React.ReactNode }) {
  return <div className="k-form">{children}</div>;
}

export function FormColumns({ children }: { children: React.ReactNode }) {
  return <div className="k-forms">{children}</div>;
}

export interface TabItem {
  id: string;
  label: string;
  /** Shown but not selectable (aria-disabled); say why with `why` on Tabs. */
  disabled?: boolean;
  /** T-087: the disclosure registry id; below its tier the tab is not rendered. */
  elementId?: string;
}

/** A tab strip with arrow-key movement (disabled tabs are skipped); the panel is the caller's. */
export function Tabs({ tabs: all, value, onValueChange, why, 'aria-label': label }: { tabs: readonly TabItem[]; value: string; onValueChange: (id: string) => void; why?: React.ReactNode; 'aria-label': string }) {
  const disclosure = useDisclosureValue();
  const tabs = all.filter((t) => disclosed(disclosure, t.elementId));
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    for (let step = 1; step <= tabs.length; step++) {
      const n = (i + d * step + tabs.length * step) % tabs.length;
      if (tabs[n]?.disabled) continue;
      refs.current[n]?.focus();
      onValueChange(tabs[n]!.id);
      return;
    }
  };
  return (
    <div className="k-tabs-wrap">
      <div role="tablist" aria-label={label} className="k-tabs">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            data-kit="tab"
            aria-selected={t.id === value}
            aria-disabled={t.disabled ? true : undefined}
            tabIndex={t.id === value && !t.disabled ? 0 : -1}
            className="k-tab"
            onClick={() => {
              if (!t.disabled) onValueChange(t.id);
            }}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {why && tabs.some((t) => t.disabled) ? <DisabledWhy>{why}</DisabledWhy> : null}
    </div>
  );
}

/** A status pill: neutral, ok, warn, bad or interactive. */
export function Tag({ tone, children }: { tone?: 'ok' | 'warn' | 'bad' | 'int'; children: React.ReactNode }) {
  return (
    <span className="k-tag" data-tone={tone}>
      {children}
    </span>
  );
}

/** An empty state: what would appear here, and one next action (kit rule). */
export function EmptyState({ title, body, action }: { title: string; body?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="k-empty" data-kit="empty">
      <b>{title}</b>
      {body ? <span className="k-meta">{body}</span> : null}
      {action}
    </div>
  );
}
